import { useEffect, useRef, useState } from 'react';
import type { CreativeAsset } from '../../types/metaAdDraft';
import { extractVideoThumbnail } from '../../utils/extractVideoThumbnail';
import { getAssetUploadUrl, putFileWithProgress } from './api';
import type { UploadedImage } from './useAssetUpload';

// D3.4b — auto-extract a video slot's thumbnail on upload. One hook instance per
// video slot (primary feed/story + every additional-ad feed/story).
//
// FLOW: the video AssetUploader reports the selected local File
// (onVideoFileSelected) the moment a valid video is chosen; extraction +
// the thumbnail's own signed-URL upload run in PARALLEL with the video's GCS
// upload. The extracted thumbnail is held as `pending` and only APPLIED to the
// draft by an effect once (a) the video slot's url is set — so the apply always
// merges through a FRESH render closure and can never be clobbered by the video
// write — and (b) the thumbnail slot is still EMPTY (the simpler D3.4b rule: a
// custom thumbnail is never silently overwritten; "Re-extract" is the explicit
// force path).
//
// Extraction failure NEVER blocks the video upload — status flips to 'failed'
// and the UI falls back to the manual thumbnail uploader (validation still
// requires a thumbnail where it always did).

export type AutoThumbnailStatus =
  | 'idle' // nothing in flight; no auto-thumbnail note
  | 'extracting' // frame grab + thumbnail upload in flight
  | 'pending' // thumbnail uploaded, waiting for the video slot to land
  | 'done' // applied — show the "Auto-generated from first frame" caption
  | 'failed'; // extraction/upload failed — show the manual-fallback note

type PendingThumb = { img: UploadedImage; force: boolean };

export type UseAutoThumbnail = {
  status: AutoThumbnailStatus;
  // Wire to the VIDEO AssetUploader's onFileSelected.
  onVideoFileSelected: (file: File) => void;
  // Re-run extraction from the kept File and OVERWRITE the current thumbnail.
  // Only possible while the File from this session is still around.
  reExtract: () => void;
  canReExtract: boolean;
};

export function useAutoThumbnail(opts: {
  accountId: string;
  slot: CreativeAsset; // the video slot this thumbnail belongs to (fresh per render)
  apply: (img: UploadedImage) => void; // writes thumbnailUrl/Path onto the slot
}): UseAutoThumbnail {
  const { accountId, slot } = opts;
  const [status, setStatus] = useState<AutoThumbnailStatus>('idle');
  const [pending, setPending] = useState<PendingThumb | null>(null);
  const fileRef = useRef<File | null>(null);
  // Latest-render apply closure — the effect below always calls a fresh one.
  const applyRef = useRef(opts.apply);
  applyRef.current = opts.apply;
  // Ignore completions of superseded runs (e.g. the AM picked another video
  // while the first extraction was still in flight).
  const runIdRef = useRef(0);

  async function run(file: File, force: boolean): Promise<void> {
    const runId = ++runIdRef.current;
    setPending(null);
    setStatus('extracting');
    try {
      const blob = await extractVideoThumbnail(file);
      const thumbName = `${file.name.replace(/\.[^.]+$/, '')}-thumb.jpg`;
      const thumbFile = new File([blob], thumbName, { type: 'image/jpeg' });
      const { uploadUrl, imageUrl, imagePath, requiredHeaders } = await getAssetUploadUrl({
        accountId,
        contentType: 'image/jpeg',
        originalFilename: thumbName,
      });
      await putFileWithProgress(uploadUrl, thumbFile, requiredHeaders);
      if (runIdRef.current !== runId) return; // superseded
      setPending({ img: { imageUrl, imagePath }, force });
      setStatus('pending');
    } catch {
      if (runIdRef.current !== runId) return;
      setStatus('failed');
    }
  }

  const onVideoFileSelected = (file: File) => {
    fileRef.current = file;
    void run(file, false);
  };

  const reExtract = () => {
    if (fileRef.current) void run(fileRef.current, true);
  };

  // Apply once the video slot has landed. Runs on fresh state, so the thumbnail
  // write merges into the slot AFTER the video write — no clobber race.
  const slotUrl = slot.url;
  const slotThumbUrl = slot.thumbnailUrl;
  useEffect(() => {
    if (!pending) return;
    if (!slotUrl) return; // video upload still in flight (or it failed — keep waiting harmlessly)
    if (pending.force || !slotThumbUrl) {
      applyRef.current(pending.img);
      setStatus('done');
    } else {
      // A thumbnail appeared meanwhile (custom upload / preserved on re-upload) —
      // never overwrite it silently.
      setStatus('idle');
    }
    setPending(null);
  }, [pending, slotUrl, slotThumbUrl]);

  return {
    status,
    onVideoFileSelected,
    reExtract,
    canReExtract: fileRef.current !== null && status !== 'extracting',
  };
}
